<script setup lang="ts">
  import { useDevices, useNetworkStatus, useToast } from '@bv/composables';
  import { useSipStore } from '@bv/stores/voip/sip';
  import { sip } from '@bv/utils';
  import { storeToRefs } from 'pinia';
  import { computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import LivePanel from '@/assets/images/layout/header/new/live-panel.svg';
  import Speaker from '@/assets/images/layout/header/new/speaker.svg';
  import Wifi from '@/assets/images/layout/header/new/wifi.svg';
  import Calculator from '@/assets/images/layout/navbar/call-center/calculator.svg';
  import PermissionHelper from '@/helpers/auth/permissions.helper';
  import { useSocketStore } from '@/stores/socket/socket.store';

  import RefreshIcon from './RefreshIcon.vue';

  interface ConnectivityItem {
    id: string
    title: string
    name: string
    icon: string
    value: boolean
  }

  const { t } = useI18n();
  const { hasMic } = useDevices();
  const { isOnline } = useNetworkStatus();
  const toast = useToast();

  const socketStore = useSocketStore();
  const sipStore = useSipStore();
  const { getStatus } = storeToRefs(socketStore);
  const { isRegistered } = storeToRefs(sipStore);

  const statusDotColor = computed(() =>
    getStatus.value === 'connected' && isOnline.value && hasMic.value && isRegistered.value
      ? 'success-400'
      : 'error-400',
  );

  const items = computed<ConnectivityItem[]>(() => [
    {
      id: 'webphone',
      title: t('header.status.webphone_status'),
      name: 'webphone',
      icon: Calculator,
      value: isRegistered.value && PermissionHelper.checkAuth('read-webphone'),
    },
    {
      id: 'livepanel',
      title: t('header.status.livepanel_status'),
      name: 'livepanel',
      icon: LivePanel,
      value: getStatus.value === 'connected',
    },
    {
      id: 'internet',
      title: t('header.status.internet_state'),
      name: 'internet',
      icon: Wifi,
      value: isOnline.value,
    },
    {
      id: 'sound',
      title: t('header.status.sound_state'),
      name: 'sound',
      icon: Speaker,
      value: hasMic.value,
    },
  ]);

  const updateNetworkStatus = () => {
    if (isOnline.value) {
      sip.register();
    } else {
      sip.unregister();
    }
  };

  watch(isOnline, () => {
    updateNetworkStatus();
  });

  const register = () => {
    if (sip.isCurrentServiceOffline) {
      toast.warning(t('header.warnings.manual_offline'));
      return;
    }
    sip.register();
  };
</script>

<template>
  <bv-menu>
    <template #activator="{ props }">
      <bv-btn v-bind="props"
              class="h-10 w-10 p-0"
              color="currentcolor"
              variant="text">
        <bv-icon
          class="absolute top-2 right-2"
          :color="statusDotColor"
          icon="bvi-circle-solid"
          :size="12"
        />
        <bv-icon icon="bvi-screen-mirror"
                 size="lg" />
      </bv-btn>
    </template>

    <div
      class="absolute end-0 top-full z-10 mt-2 flex w-[175px] flex-col gap-2 rounded-xl bg-(--shadesWhite) p-4 shadow-[0px_8px_32px_0px_rgba(0,0,0,0.08)]"
    >
      <div v-for="item in items"
           :key="item.id"
           class="flex shrink-0 gap-2">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-(--gray50) p-1">
          <img :alt="item.name"
               :src="item.icon"
               :title="item.name" />
        </div>

        <div class="flex shrink-0 flex-col gap-1">
          <h3 class="text-xs font-normal leading-[14px] text-(--gray700)">
            {{ item.title }}
          </h3>

          <div
            class="text-xs font-normal capitalize leading-3"
            :class="item.value ? 'text-(--green500)' : 'text-(--error500)'"
          >
            <button v-if="!item.value && item.name === 'webphone'"
                    type="button"
                    @click="register">
              {{ item.value ? t('header.status.connected') : t('header.status.disconnected') }}
              <refresh-icon class="inline-block size-3" />
            </button>

            <template v-else>
              {{ item.value ? t('header.status.connected') : t('header.status.disconnected') }}
            </template>
          </div>
        </div>
      </div>
    </div>
  </bv-menu>
</template>
